/**
 * Calculation Profiles Management Page
 * Admin page for managing interest calculation profiles.
 * System profiles are read-only (except active toggle); custom profiles support full CRUD.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import FunctionsIcon from '@mui/icons-material/Functions';
import BackspaceIcon from '@mui/icons-material/Backspace';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiClient';
import { useNotification } from '../../contexts/NotificationContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SortableHeaderCell from '../../components/common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';

const compoundingLabels = {
  365: 'Daily',
  12: 'Monthly',
  4: 'Quarterly',
  1: 'Yearly',
};

const buildCalcProfileColumns = (usageCounts) => [
  { key: 'name',                  label: 'Name',             align: 'left',   getValue: (p) => p.name },
  { key: 'interest_method',       label: 'Method',           align: 'left',   getValue: (p) => p.interest_method },
  { key: 'day_count_convention',  label: 'Day Count',        align: 'left',   getValue: (p) => p.day_count_convention },
  { key: 'day_count_method',      label: 'Day Count Method', align: 'left',   getValue: (p) => p.day_count_method || 'Inclusive' },
  { key: 'period_boundary_convention', label: 'Boundary',    align: 'left',   getValue: (p) => p.period_boundary_convention || 'anniversary-closes' },
  { key: 'compounding_frequency', label: 'Compounding',      align: 'left',   getValue: (p) => p.compounding_frequency },
  { key: 'formula_display',       label: 'Formula',          align: 'left',   getValue: (p) => p.formula_display },
  { key: 'usage',                 label: 'Contracts',        align: 'center', getValue: (p) => usageCounts[p.id] || 0 },
  { key: 'active',                label: 'Status',           align: 'center', getValue: (p) => p.active ? 1 : 0 },
  { key: 'is_system',             label: 'Type',             align: 'center', getValue: (p) => p.is_system ? 'System' : 'Custom' },
];

// Available fields that map to actual contract/profile database columns
const FORMULA_FIELDS = [
  { token: 'Principal', dbField: 'capital_amount', source: 'contract', description: 'Investment capital amount' },
  { token: 'Rate', dbField: 'interest_rate', source: 'contract', description: 'Annual interest rate (%)' },
  { token: 'Days', dbField: 'period_days', source: 'contract dates', description: 'Actual days in accrual period' },
];

// Operators and constants available for formula building
const FORMULA_OPERATORS = [
  { token: 'x', label: 'x', description: 'Multiply' },
  { token: '/', label: '/', description: 'Divide' },
  { token: '+', label: '+', description: 'Add' },
  { token: '-', label: '-', description: 'Subtract' },
  { token: '(', label: '(', description: 'Open bracket' },
  { token: ')', label: ')', description: 'Close bracket' },
  { token: '^', label: '^', description: 'Power/Exponent' },
  { token: '=', label: '=', description: 'Equals' },
];

const FORMULA_KEYWORDS = [
  { token: 'Interest', label: 'Interest', description: 'Result label' },
];

const chipSx = {
  fontFamily: 'monospace',
  fontWeight: 600,
  fontSize: '0.75rem',
  cursor: 'pointer',
};

const fieldChipSx = {
  ...chipSx,
  backgroundColor: 'rgba(76, 175, 80, 0.08)',
  borderColor: '#4CAF50',
  color: '#2e7d32',
  '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.18)', borderColor: '#388e3c' },
};

const operatorChipSx = {
  ...chipSx,
  backgroundColor: 'rgba(25, 118, 210, 0.08)',
  borderColor: '#1976d2',
  color: '#1565c0',
  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.18)', borderColor: '#1565c0' },
};

const keywordChipSx = {
  ...chipSx,
  backgroundColor: 'rgba(156, 39, 176, 0.08)',
  borderColor: '#9c27b0',
  color: '#7b1fa2',
  '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.18)', borderColor: '#7b1fa2' },
};

const defaultFormData = {
  name: '',
  description: '',
  interest_method: 'Simple',
  day_count_convention: 'Actual/365',
  day_count_basis: 365,
  compounding_frequency: null,
  rounding_precision: 2,
  formula_display: buildFormulaPreview('Simple', 'Actual/365', null),
  day_count_method: 'Inclusive',
  period_boundary_convention: 'anniversary-closes',
  active: true,
};

function validateFormula(formula) {
  if (!formula) return [];
  const warnings = [];
  if (!formula.includes('Principal')) warnings.push('Missing "Principal"');
  if (!formula.includes('Rate')) warnings.push('Missing "Rate"');
  if (!formula.includes('Days')) warnings.push('Missing "Days"');
  if (!formula.includes('=')) warnings.push('Missing "=" sign');
  const opens = (formula.match(/\(/g) || []).length;
  const closes = (formula.match(/\)/g) || []).length;
  if (opens !== closes) warnings.push(`Unbalanced parentheses (${opens} open, ${closes} close)`);
  return warnings;
}

function buildFormulaPreview(method, convention, compFreq) {
  const basis = (convention === '30/360' || convention === 'Actual/360') ? 360 : 365;
  if (method === 'Simple') {
    return `Interest = Principal x Rate x (Actual Days / ${basis})`;
  }
  if (method === 'Compound Linear') {
    return `Daily Amount = (Principal x (1 + Rate)^(Term Days / ${basis}) - Principal) / Term Days; Period Interest = Daily Amount x Days in Period`;
  }
  if (method === 'Simple Periodic Capitalization') {
    return `Within period: Interest = Principal x Rate x (Actual Days / ${basis}). Principal capitalizes (steps up by accrued interest) at each period boundary.`;
  }
  const n = compFreq || 12;
  if (n === 1) {
    return `Interest = Principal x (1 + Rate)^(Actual Days / ${basis}) - Principal`;
  }
  return `Interest = Principal x (1 + Rate/${n})^(${n} x Actual Days / ${basis}) - Principal`;
}

function CalculationProfilesPage() {
  const notify = useNotification();
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [usageCounts, setUsageCounts] = useState({});
  const sort = useTableSort();
  const profileColumns = buildCalcProfileColumns(usageCounts);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [formData, setFormData] = useState({ ...defaultFormData });
  const [saving, setSaving] = useState(false);

  // Delete confirmation state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Reset-to-defaults confirmation state
  const [resetDialog, setResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Formula cursor position ref
  const formulaRef = useRef(null);

  const loadProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiGet('/refdata/calculation-profiles?active_only=false');
      const data = response.data || [];
      setProfiles(data);

      // Load usage counts for each profile
      const counts = {};
      await Promise.all(
        data.map(async (profile) => {
          try {
            const usage = await apiGet(`/refdata/calculation-profiles/${profile.id}/usage`);
            counts[profile.id] = usage.data?.contract_count || 0;
          } catch {
            counts[profile.id] = 0;
          }
        })
      );
      setUsageCounts(counts);
    } catch (err) {
      notify.error('Failed to load calculation profiles: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notify is stable
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const handleOpenCreate = () => {
    const defaults = { ...defaultFormData };
    defaults.formula_display = buildFormulaPreview(
      defaults.interest_method,
      defaults.day_count_convention,
      defaults.compounding_frequency
    );
    setFormData(defaults);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleOpenEdit = (profile) => {
    setFormData({
      id: profile.id,
      name: profile.name,
      description: profile.description || '',
      interest_method: profile.interest_method,
      day_count_convention: profile.day_count_convention,
      day_count_basis: profile.day_count_basis,
      compounding_frequency: profile.compounding_frequency,
      rounding_precision: profile.rounding_precision || 2,
      formula_display: profile.formula_display || '',
      day_count_method: profile.day_count_method || 'Inclusive',
      period_boundary_convention: profile.period_boundary_convention || 'anniversary-closes',
      active: profile.active,
      is_system: profile.is_system,
      system_key: profile.system_key || null,
    });
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const updateFormField = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      // Auto-derive day_count_basis from convention
      if (field === 'day_count_convention') {
        next.day_count_basis = (value === '30/360' || value === 'Actual/360') ? 360 : 365;
      }

      // Clear compounding frequency for Simple; set defaults for compound types
      if (field === 'interest_method' && value === 'Simple') {
        next.compounding_frequency = null;
      }
      if (field === 'interest_method' && value === 'Compound' && !next.compounding_frequency) {
        next.compounding_frequency = 12;
      }
      if (field === 'interest_method' && value === 'Compound Linear') {
        next.compounding_frequency = 1; // Yearly compounding for linear method
      }
      if (field === 'interest_method' && value === 'Simple Periodic Capitalization' && !next.compounding_frequency) {
        next.compounding_frequency = 1; // Default to annual
      }
      if (field === 'interest_method' && value === 'Annuity' && !next.compounding_frequency) {
        next.compounding_frequency = 12; // Default to monthly — matches seeded ANNUITY_MONTHLY profile
      }

      // Auto-generate formula preview
      if (['interest_method', 'day_count_convention', 'compounding_frequency'].includes(field)) {
        next.formula_display = buildFormulaPreview(
          next.interest_method,
          next.day_count_convention,
          next.compounding_frequency
        );
      }

      return next;
    });
  };

  // Insert a token into the formula at cursor position or at the end
  const insertFormulaToken = (token) => {
    setFormData((prev) => {
      const current = prev.formula_display || '';
      const input = formulaRef.current?.querySelector('textarea');
      if (input) {
        const start = input.selectionStart || current.length;
        const end = input.selectionEnd || current.length;
        const before = current.substring(0, start);
        const after = current.substring(end);
        // Add space padding around tokens for readability
        const needsSpaceBefore = before.length > 0 && !before.endsWith(' ') && !before.endsWith('(');
        const prefix = needsSpaceBefore ? ' ' : '';
        const newFormula = before + prefix + token + after;
        // Set cursor position after insert
        const newPos = (before + prefix + token).length;
        setTimeout(() => {
          input.selectionStart = newPos;
          input.selectionEnd = newPos;
          input.focus();
        }, 0);
        return { ...prev, formula_display: newFormula };
      }
      // Fallback: append to end
      const spacer = current.length > 0 && !current.endsWith(' ') ? ' ' : '';
      return { ...prev, formula_display: current + spacer + token };
    });
  };

  // Remove last token from formula
  const handleFormulaBackspace = () => {
    setFormData((prev) => {
      const current = (prev.formula_display || '').trimEnd();
      // Remove last word/token
      const lastSpace = current.lastIndexOf(' ');
      const newFormula = lastSpace > 0 ? current.substring(0, lastSpace) : '';
      return { ...prev, formula_display: newFormula };
    });
  };

  // Reset formula to auto-generated preview
  const handleFormulaReset = () => {
    setFormData((prev) => ({
      ...prev,
      formula_display: buildFormulaPreview(prev.interest_method, prev.day_count_convention, prev.compounding_frequency),
    }));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.interest_method || !formData.day_count_convention || !formData.formula_display) {
      notify.error('Name, interest method, day count convention, and formula are required');
      return;
    }

    if (formData.interest_method === 'Annuity' && !formData.compounding_frequency) {
      notify.error('Annuity profiles require a compounding frequency.');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: formData.name,
        description: formData.description || null,
        interest_method: formData.interest_method,
        day_count_convention: formData.day_count_convention,
        day_count_basis: parseInt(formData.day_count_basis),
        compounding_frequency: ['Compound', 'Compound Linear', 'Simple Periodic Capitalization', 'Annuity'].includes(formData.interest_method) ? parseInt(formData.compounding_frequency) : null,
        rounding_precision: parseInt(formData.rounding_precision) || 2,
        formula_display: formData.formula_display,
        day_count_method: formData.day_count_method || 'Inclusive',
        period_boundary_convention: formData.period_boundary_convention || 'anniversary-closes',
        active: formData.active,
      };

      if (dialogMode === 'create') {
        await apiPost('/refdata/calculation-profiles', payload);
        notify.success('Calculation profile created successfully');
      } else {
        await apiPut(`/refdata/calculation-profiles/${formData.id}`, payload);
        notify.success('Calculation profile updated successfully');
      }

      setDialogOpen(false);
      loadProfiles();
    } catch (err) {
      notify.error('Failed to save: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (profile) => {
    const count = usageCounts[profile.id] || 0;
    if (profile.active && count > 0) {
      notify.error(`Cannot deactivate "${profile.name}" — it is used by ${count} contract(s). Reassign contracts first.`);
      return;
    }

    try {
      await apiPut(`/refdata/calculation-profiles/${profile.id}`, {
        active: !profile.active,
      });
      notify.success(`Profile ${profile.active ? 'deactivated' : 'activated'} successfully`);
      loadProfiles();
    } catch (err) {
      notify.error('Failed to toggle status: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDeleteClick = (profile) => {
    const count = usageCounts[profile.id] || 0;
    if (count > 0) {
      notify.error(`Cannot delete "${profile.name}" — it is used by ${count} contract(s). Reassign contracts first.`);
      return;
    }
    setDeleteTarget(profile);
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await apiDelete(`/refdata/calculation-profiles/${deleteTarget.id}`);
      notify.success('Calculation profile deleted successfully');
      setDeleteDialog(false);
      setDeleteTarget(null);
      loadProfiles();
    } catch (err) {
      notify.error('Failed to delete: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleting(false);
    }
  };

  const handleResetDefaultsConfirm = async () => {
    if (!formData.id) return;
    try {
      setResetting(true);
      const response = await apiPost(`/refdata/calculation-profiles/${formData.id}/reset-defaults`);
      const restored = response.data || {};
      setFormData((prev) => ({
        ...prev,
        name: restored.name ?? prev.name,
        description: restored.description ?? '',
        interest_method: restored.interest_method ?? prev.interest_method,
        day_count_convention: restored.day_count_convention ?? prev.day_count_convention,
        day_count_basis: restored.day_count_basis ?? prev.day_count_basis,
        compounding_frequency: restored.compounding_frequency ?? null,
        rounding_precision: restored.rounding_precision ?? 2,
        formula_display: restored.formula_display ?? '',
        day_count_method: restored.day_count_method ?? 'Inclusive',
        period_boundary_convention: restored.period_boundary_convention ?? 'anniversary-closes',
      }));
      setResetDialog(false);
      notify.success('Profile reset to defaults');
      loadProfiles();
    } catch (err) {
      notify.error('Failed to reset: ' + (err.message || 'Unknown error'));
    } finally {
      setResetting(false);
    }
  };

  // Get dynamic basis/compounding values for the formula chip display
  const basisValue = formData.day_count_basis || 365;
  const compFreqValue = formData.compounding_frequency || 12;
  const isSystemEdit = dialogMode === 'edit' && !!formData.is_system;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Calculation Profiles</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadProfiles}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Add Profile
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage interest calculation profiles. System profiles can be fully edited but cannot be deleted — use &quot;Reset to Defaults&quot; to restore the original built-in values.
        Each profile defines the interest method, day count convention, and compounding frequency.
      </Typography>
      <Paper>
        {loading ? (
          <LoadingSpinner />
        ) : profiles.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <FunctionsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No calculation profiles configured
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={{ mt: 2 }}>
              Add First Profile
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {profileColumns.map((col) => (
                    <SortableHeaderCell key={col.key} sort={sort} columnKey={col.key} align={col.align}>
                      {col.label}
                    </SortableHeaderCell>
                  ))}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sort.sortRows(profiles, profileColumns).map((profile) => (
                  <TableRow key={profile.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {profile.name}
                      </Typography>
                      {profile.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {profile.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={profile.interest_method}
                        size="small"
                        color={profile.interest_method.startsWith('Compound') ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{profile.day_count_convention}</TableCell>
                    <TableCell>
                      <Chip
                        label={profile.day_count_method || 'Inclusive'}
                        size="small"
                        variant="outlined"
                        color={profile.day_count_method === 'Exclusive' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {profile.interest_method === 'Compound'
                        ? compoundingLabels[profile.compounding_frequency] || `${profile.compounding_frequency}x/year`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {profile.formula_display}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={usageCounts[profile.id] || 0}
                        size="small"
                        variant="outlined"
                        color={usageCounts[profile.id] > 0 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={profile.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={profile.active ? 'success' : 'default'}
                        onClick={() => handleToggleActive(profile)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={profile.is_system ? 'System' : 'Custom'}
                        size="small"
                        variant="outlined"
                        color={profile.is_system ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenEdit(profile)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!profile.is_system && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(profile)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>
              {dialogMode === 'create' ? 'Add Calculation Profile' : 'Edit Calculation Profile'}
            </span>
            {isSystemEdit && (
              <Chip label="System" size="small" color="info" variant="outlined" />
            )}
          </Box>
          {dialogMode === 'edit' && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={(e) => updateFormField('active', e.target.checked)}
                  size="small"
                />
              }
              label={formData.active ? 'Active' : 'Inactive'}
              sx={{ mr: 0 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Left Column — Profile Settings */}
            <Grid
              size={{
                xs: 12,
                md: 5
              }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Name"
                  value={formData.name}
                  onChange={(e) => updateFormField('name', e.target.value)}
                  required
                  placeholder="e.g., Compound Quarterly Actual/365"
                  size="small"
                />

                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => updateFormField('description', e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Optional description"
                  size="small"
                />

                <TextField
                  select
                  label="Interest Method"
                  value={formData.interest_method}
                  onChange={(e) => updateFormField('interest_method', e.target.value)}
                  required
                  size="small"
                >
                  <MenuItem value="Simple">Simple</MenuItem>
                  <MenuItem value="Compound">Compound</MenuItem>
                  <MenuItem value="Compound Linear">Compound Linear</MenuItem>
                  <MenuItem value="Simple Periodic Capitalization">Simple Periodic Capitalization</MenuItem>
                  <MenuItem value="Annuity">Annuity</MenuItem>
                </TextField>

                <TextField
                  select
                  label="Day Count Convention"
                  value={formData.day_count_convention}
                  onChange={(e) => updateFormField('day_count_convention', e.target.value)}
                  required
                  size="small"
                >
                  <MenuItem value="Actual/365">Actual/365 (365 days, ignores leap days)</MenuItem>
                  <MenuItem value="Actual/360">Actual/360 (actual days / 360)</MenuItem>
                  <MenuItem value="30/360">30/360 (legacy label — same math as Actual/360)</MenuItem>
                </TextField>

                <TextField
                  select
                  label="Day Count Method"
                  value={formData.day_count_method || 'Inclusive'}
                  onChange={(e) => updateFormField('day_count_method', e.target.value)}
                  size="small"
                  helperText="Inclusive counts both start and end dates; Exclusive excludes end date"
                >
                  <MenuItem value="Inclusive">Inclusive (Start + End)</MenuItem>
                  <MenuItem value="Exclusive">Exclusive (Start only)</MenuItem>
                </TextField>

                <TextField
                  select
                  label="Period Boundary Convention"
                  value={formData.period_boundary_convention || 'anniversary-closes'}
                  onChange={(e) => updateFormField('period_boundary_convention', e.target.value)}
                  size="small"
                  helperText="Anniversary day either closes the pre-cap period (legacy) or opens the post-cap period (bank-standard for SPC)"
                >
                  <MenuItem value="anniversary-closes">Anniversary closes pre-cap period (legacy)</MenuItem>
                  <MenuItem value="anniversary-opens">Anniversary opens post-cap period (SPC)</MenuItem>
                </TextField>

                {(formData.interest_method === 'Compound' || formData.interest_method === 'Compound Linear' || formData.interest_method === 'Simple Periodic Capitalization') && (
                  <TextField
                    select
                    label="Compounding Frequency"
                    value={formData.compounding_frequency || 12}
                    onChange={(e) => updateFormField('compounding_frequency', parseInt(e.target.value))}
                    required
                    size="small"
                  >
                    <MenuItem value={365}>Daily (365x/year)</MenuItem>
                    <MenuItem value={12}>Monthly (12x/year)</MenuItem>
                    <MenuItem value={4}>Quarterly (4x/year)</MenuItem>
                    <MenuItem value={1}>Yearly (1x/year)</MenuItem>
                  </TextField>
                )}

                {formData.interest_method === 'Annuity' && (
                  <TextField
                    select
                    label="Compounding Frequency"
                    value={formData.compounding_frequency || ''}
                    onChange={(e) => updateFormField('compounding_frequency', parseInt(e.target.value))}
                    required
                    error={!formData.compounding_frequency}
                    helperText={!formData.compounding_frequency ? 'Annuity profiles require a compounding frequency.' : ''}
                    size="small"
                  >
                    <MenuItem value={12}>Monthly (12x/year)</MenuItem>
                    <MenuItem value={4}>Quarterly (4x/year)</MenuItem>
                    <MenuItem value={1}>Annual (1x/year)</MenuItem>
                  </TextField>
                )}

                <TextField
                  label="Rounding Precision"
                  type="number"
                  value={formData.rounding_precision}
                  onChange={(e) => updateFormField('rounding_precision', e.target.value)}
                  inputProps={{ min: 0, max: 8 }}
                  helperText="Decimal places for rounding"

                  size="small"
                />

              </Box>
            </Grid>

            {/* Right Column — Formula Builder & Field Mapping */}
            <Grid
              size={{
                xs: 12,
                md: 7
              }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Formula Builder */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">
                    Formula Display
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Remove last token">
                      <IconButton size="small" onClick={handleFormulaBackspace}>
                        <BackspaceIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset to auto-generated formula">
                      <IconButton size="small" onClick={handleFormulaReset}>
                        <RestartAltIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <TextField
                  ref={formulaRef}
                  value={formData.formula_display}
                  onChange={(e) => updateFormField('formula_display', e.target.value)}
                  required
                  multiline
                  rows={2}
                  fullWidth
                  size="small"
                  error={validateFormula(formData.formula_display).some(w => w.includes('Unbalanced'))}
                  sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.9rem' } }}
                  helperText="Auto-generated from settings. Click fields/operators below to build, or type directly."
                />

                {/* Formula validation warnings */}
                {formData.formula_display && validateFormula(formData.formula_display).length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {validateFormula(formData.formula_display).map((warning) => (
                      <Chip
                        key={warning}
                        label={warning}
                        size="small"
                        color={warning.includes('Unbalanced') ? 'error' : 'warning'}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                )}

                <Box>
                  {/* Available Fields */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Available Fields (click to insert)
                  </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {FORMULA_FIELDS.map((field) => (
                        <Tooltip key={field.token} title={`${field.dbField} — ${field.description} (${field.source})`}>
                          <Chip
                            label={field.token}
                            size="small"
                            variant="outlined"
                            onClick={() => insertFormulaToken(field.token)}
                            sx={fieldChipSx}
                          />
                        </Tooltip>
                      ))}
                      <Tooltip title={`day_count_basis = ${basisValue} (from Day Count Convention)`}>
                        <Chip
                          label={String(basisValue)}
                          size="small"
                          variant="outlined"
                          onClick={() => insertFormulaToken(String(basisValue))}
                          sx={fieldChipSx}
                        />
                      </Tooltip>
                      {(formData.interest_method === 'Compound' || formData.interest_method === 'Compound Linear' || formData.interest_method === 'Simple Periodic Capitalization') && (
                        <Tooltip title={`compounding_frequency = ${compFreqValue} (from Compounding Frequency)`}>
                          <Chip
                            label={String(compFreqValue)}
                            size="small"
                            variant="outlined"
                            onClick={() => insertFormulaToken(String(compFreqValue))}
                            sx={fieldChipSx}
                          />
                        </Tooltip>
                      )}
                    </Box>

                    {/* Keywords & Operators */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Keywords & Operators
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {FORMULA_KEYWORDS.map((kw) => (
                        <Chip
                          key={kw.token}
                          label={kw.token}
                          size="small"
                          variant="outlined"
                          onClick={() => insertFormulaToken(kw.token)}
                          sx={keywordChipSx}
                        />
                      ))}
                      <Chip label="1" size="small" variant="outlined" onClick={() => insertFormulaToken('1')} sx={keywordChipSx} />
                      {FORMULA_OPERATORS.map((op) => (
                        <Tooltip key={op.token} title={op.description}>
                          <Chip
                            label={op.label}
                            size="small"
                            variant="outlined"
                            onClick={() => insertFormulaToken(op.token)}
                            sx={operatorChipSx}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>

                {/* Field mapping info */}
                <Alert severity="info" icon={false} sx={{ py: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                    How this profile affects calculations:
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', lineHeight: 1.8 }}>
                    <b>Principal</b> &rarr; <code>capital_amount</code> (contract)<br/>
                    <b>Rate</b> &rarr; <code>interest_rate</code> (contract)<br/>
                    <b>Days</b> &rarr; accrual period days (contract dates)<br/>
                    <b>{formData.day_count_basis}</b> &rarr; this profile's <b>Day Count Convention</b>
                    {(formData.interest_method === 'Compound' || formData.interest_method === 'Compound Linear' || formData.interest_method === 'Simple Periodic Capitalization') && (
                      <><br/><b>n = {formData.compounding_frequency || 12}</b> &rarr; this profile's <b>Compounding Frequency</b></>
                    )}
                  </Typography>
                </Alert>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          {isSystemEdit && formData.system_key && (
            <Button
              color="warning"
              onClick={() => setResetDialog(true)}
              disabled={saving || resetting}
            >
              Reset to Defaults
            </Button>
          )}
          {dialogMode === 'create' && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !formData.name || !formData.formula_display || validateFormula(formData.formula_display).some(w => w.includes('Unbalanced'))}
            >
              {saving ? <CircularProgress size={20} /> : 'Create'}
            </Button>
          )}
          {dialogMode === 'edit' && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !formData.name || !formData.formula_display || validateFormula(formData.formula_display).some(w => w.includes('Unbalanced'))}
            >
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* Reset to Defaults Confirmation Dialog */}
      <Dialog open={resetDialog} onClose={() => setResetDialog(false)}>
        <DialogTitle>Reset to Defaults</DialogTitle>
        <DialogContent>
          <Typography>
            Reset &quot;{formData.name}&quot; to its original built-in values?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will overwrite all current settings on this system profile — including the name, method, day count, rounding precision and formula — with the canonical defaults. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog(false)} disabled={resetting}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetDefaultsConfirm}
            disabled={resetting}
          >
            {resetting ? <CircularProgress size={20} /> : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Calculation Profile</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{deleteTarget?.name}&quot;?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. Profiles linked to contracts cannot be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CalculationProfilesPage;
